import { IResEducation, IResExperiences, IResPersonal, IResumeData } from '@/lib/interfaces';
import React, { useEffect, useRef, useState } from 'react'
import { Loader2, UploadCloud } from "lucide-react";
import { getActionsCost, ResumeParse } from './actions';
import CartaTokens from '@/../public/images/tokens_sm.png';
import Image from 'next/image';


interface IQuickImport {
  setPersonal: (personal: IResPersonal) => void,
  setRole: (role: string) => void,
  setExperiences: (experiences: IResExperiences[]) => void,
  setEducation: (education: IResEducation[]) => void,
  setSkills: (skills: string[]) => void
}

function QuickImport({ setPersonal, setRole, setExperiences, setEducation, setSkills }: IQuickImport) {
  const [dragActive, setDragActive] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [ parseCost, setParseCost ] = useState<number>(0);
  const [ isCostLoading, setIsCostLoading ] = useState<boolean>(true);

  useEffect(() => {
    async function getActions () {
      setIsCostLoading(true);

      let a = await getActionsCost('RESPRS01');
      console.log(a.data);
      setParseCost(a.data);
      setIsCostLoading(false);
    }
    getActions();

  }, []);
  
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // Create a mock change event to reuse your existing handleFileUpload
      const mockEvent = {
        target: { files: e.dataTransfer.files }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      await handleFileUpload(mockEvent);
    }
  };

  // --- New File Upload Handler ---
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsParsing(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      // Endpoint to analyze the resume via Gemini/Multimodal
      const res = await ResumeParse(formData);

      // force user tokens balance component to refresh
		  window.dispatchEvent(new Event('tokens-deducted'));

      if (res.status == 200) {
        const data: IResumeData = res.data;
        
        // 1. Update Personal & Role (Simple objects)
        if (data.personal) setPersonal(data.personal);
        if (data.role) setRole(data.role);

        // 2. Update Experiences (Replacing the whole array)
        if (data.experiences && data.experiences.length > 0) {
          const formattedExperiences = data.experiences.map(exp => ({
            company: exp.company || "",
            role: exp.role || "",
            // Ensure we handle the date string to Date object conversion
            fromDate: exp.fromDate ? new Date(exp.fromDate) : new Date(),
            toDate: exp.toDate ? new Date(exp.toDate) : new Date(),
            responsibilities: exp.responsibilities || ""
          }));
          setExperiences(formattedExperiences);
        }

        // 3. Update Education (Replacing the whole array)
        if (data.education && data.education.length > 0) {
          setEducation(data.education);
        }

        // 4. Update Skills
        if (data.skills) setSkills(data.skills);
      }
    } catch (error) {
      console.error("Failed to parse resume:", error);
    } finally {
      setIsParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <section 
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      className={`
        relative border-2 border-dashed rounded-xl transition-all cursor-pointer mb-8
        ${dragActive 
          ? "border-indigo-500 bg-indigo-100/50 scale-[1.01]" 
          : "border-indigo-200 bg-indigo-50/20 hover:bg-indigo-50/40"
        }
        ${isParsing ? "opacity-60 cursor-not-allowed" : ""}
      `}
    >
      <input 
        type="file" 
        ref={fileInputRef} 
        className="hidden" 
        accept=".pdf,.docx,.txt" 
        onChange={handleFileUpload} 
      />

      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className={`
          p-4 rounded-full mb-4 transition-transform
          ${dragActive ? "bg-indigo-600 text-white scale-110" : "bg-indigo-100 text-indigo-600"}
        `}>
          {isParsing ? (
            <Loader2 size={32} className="animate-spin" />
          ) : (
            <UploadCloud size={32} />
          )}
        </div>

        <div className="space-y-1">
          <h3 className="text-lg font-semibold text-indigo-900">
            {isParsing ? "Analyzing your resume..." : "Import Existing Resume"}
          </h3>
          <p className="text-sm text-indigo-600/70">
            {dragActive 
              ? "Drop the file here" 
              : "Drag & drop your PDF/Docx here, or click to browse"
            }
          </p>
          <div className="flex flex-col lg:flex-row justify-center items-center gap-2">
            <div className="flex justify-center items-center text-sm">
              <span>{"("}</span><Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
              <span className="ml-1">
                {
                  isCostLoading 
                  ? <Loader2 size={32} className="animate-spin" /> 
                  : parseCost
                } 
              <span className="hidden md:inline"> tokens</span></span><span>{")"}</span>
            </div>
          </div>
        </div>

        {isParsing && (
          <div className="mt-4 w-full max-w-xs bg-indigo-100 rounded-full h-1.5 overflow-hidden">
            <div className="bg-indigo-600 h-full animate-progress-indefinite" style={{ width: '50%' }}></div>
          </div>
        )}
      </div>
    </section>
  )
}

export default QuickImport